import type { ReactNode } from 'react';
import { formatNumber } from '@/components/live';

/**
 * The row of small figures above a list, which double as filters.
 *
 * A card is not decoration: clicking it narrows the grid below to exactly the rows it counted,
 * and clicking it again clears. That is why the count and the filter are one object here -
 * a card showing "12 pending" beside a filter that selects something subtly different is how
 * people stop trusting both.
 *
 * Counts are computed from the rows already loaded, not fetched separately. The grid is
 * already showing what RLS returned, so a second query could only disagree with it - and a
 * card that says 14 above a list of 12 is worse than no card.
 */
export interface Stat {
  key: string;
  label: string;
  /** Which of the loaded rows this card counts. Also what it filters to when clicked. */
  match: (row: never) => boolean;
  tone?: 'ok' | 'warn' | 'error' | 'info' | 'neutral';
  hint?: string;
  /** Overrides the count, for a figure that is a sum rather than a tally. */
  value?: string;
}

/**
 * A second figure for the same rows (owner, 2026-09-22: "can have 2 figures in the stat cards?
 * viz. count and area"): summed from the rows the card counts, with its unit - hectares,
 * rupees, cubic metres, days.
 */
export interface StatSecondary<T> {
  /** The number each row adds. */
  sum: (row: T) => number | null | undefined;
  unit: string;
  digits?: number;
  /** Show as money (Rs, Indian grouping) instead of a number with a unit. */
  money?: boolean;
}

export function StatCards<T>({
  rows,
  stats,
  active,
  onSelect,
  secondary,
}: {
  rows: T[];
  stats: Array<Omit<Stat, 'match'> & { match: (row: T) => boolean; secondary?: StatSecondary<T> | null }>;
  active?: string | null;
  onSelect?: (key: string | null) => void;
  /** The second figure for every card, unless a card sets its own (or null for none). */
  secondary?: StatSecondary<T>;
}) {
  if (stats.length === 0) return null;
  const total = rows.length;

  return (
    <div className="stat-cards" role="group" aria-label="Summary and filters">
      {stats.map((stat) => {
        const matched = rows.filter((row) => stat.match(row));
        const count = matched.length;
        const selected = active === stat.key;
        const clickable = Boolean(onSelect);
        const second = stat.secondary === null ? null : stat.secondary ?? secondary;
        const secondValue = second ? matched.reduce((sum, row) => sum + (Number(second.sum(row)) || 0), 0) : null;
        const share = total > 0 && !stat.value ? Math.round((count / total) * 100) : null;
        return (
          <button
            aria-pressed={selected}
            className={`stat-card tone-${stat.tone ?? 'neutral'}${selected ? ' selected' : ''}${count === 0 && !stat.value ? ' is-zero' : ''}`}
            disabled={!clickable}
            key={stat.key}
            onClick={() => onSelect?.(selected ? null : stat.key)}
            title={stat.hint ?? (clickable ? `Show only ${stat.label.toLowerCase()}` : undefined)}
            type="button"
          >
            <span className="stat-label">{stat.label}</span>
            <span className="stat-figures">
              <strong className="stat-value">{stat.value ?? formatNumber(count)}</strong>
              {second && secondValue !== null && (count > 0 || stat.value) && (
                <span className="stat-second">
                  {second.money ? `₹${formatNumber(secondValue, 0)}` : formatNumber(secondValue, second.digits ?? 2)}
                  {!second.money && <em>{second.unit}</em>}
                </span>
              )}
            </span>
            {share !== null && (
              <span aria-label={`${share}% of all`} className="stat-share" role="img">
                <span style={{ width: `${Math.max(share, count > 0 ? 3 : 0)}%` }} />
              </span>
            )}
            {stat.hint && <small>{stat.hint}</small>}
          </button>
        );
      })}
    </div>
  );
}

/** Apply the selected card to the rows. One place, so every screen filters identically. */
export function applyStat<T>(
  rows: T[],
  stats: Array<{ key: string; match: (row: T) => boolean }>,
  active: string | null | undefined,
): T[] {
  if (!active) return rows;
  const stat = stats.find((entry) => entry.key === active);
  return stat ? rows.filter((row) => stat.match(row)) : rows;
}

export function StatTile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="stat-card tone-neutral" style={{ cursor: 'default' }}>
      <span className="stat-label">{label}</span>
      <strong className="stat-value">{value}</strong>
      {hint && <small>{hint}</small>}
    </div>
  );
}
